import type { ImportInfo } from '@mcgames/contracts';
import { Button, Select } from '@mcgames/ui';
import { useId, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormatBytes } from '../../lib/format-bytes.ts';
import { ErrorText } from './ErrorText.tsx';
import { useRetryImport } from './import-hooks.ts';
import {
  STEP_TYPES,
  draftToStep,
  newStepDraft,
  type StepDraft,
  type StepType,
} from './install-draft.ts';
import { StepEditor } from './StepEditor.tsx';
import styles from './Games.module.css';

/**
 * For an import whose release matched no install pattern: the files of the fetched release and an
 * editor for the install steps the import continues with (they replace the detection result).
 */
export function ImportStepsForm({ gameId, info }: { gameId: string; info: ImportInfo }) {
  const { t } = useTranslation('admin');
  const formatBytes = useFormatBytes();
  const retry = useRetryImport(gameId, info.id);
  const headingId = useId();
  const [steps, setSteps] = useState<StepDraft[]>([]);
  const [newType, setNewType] = useState<StepType>('extract');
  const staged = info.staged;

  function move(index: number, direction: -1 | 1) {
    const target = index + direction;
    const next = [...steps];
    const [step, other] = [next[index], next[target]];
    if (step === undefined || other === undefined) return;
    next[index] = other;
    next[target] = step;
    setSteps(next);
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    retry.mutate({ installSteps: steps.map(draftToStep) });
  }

  return (
    <form className={styles['form']} aria-labelledby={headingId} onSubmit={submit}>
      {staged !== null && (
        <>
          <h3 className={styles['subheading']}>{t('games.import.releaseFiles')}</h3>
          <ul className={`${styles['fileList']} ${styles['importFiles']}`}>
            {staged.files.map((file) => (
              <li key={file.name}>
                <span className={styles['fileName']}>{file.name}</span>
                <span className={styles['fileSize']}>{formatBytes(file.size)}</span>
              </li>
            ))}
          </ul>
          <p className={styles['muted']}>
            {t('games.nzb.totalSize', { size: formatBytes(staged.downloadSize) })}
          </p>
          {staged.hasPassword && (
            <p className={styles['hint']}>{t('games.import.releasePassword')}</p>
          )}
        </>
      )}
      <h3 id={headingId} className={styles['subheading']}>
        {t('games.import.stepsTitle')}
      </h3>
      <p className={styles['hint']}>{t('games.import.stepsHint')}</p>
      {steps.length > 0 && (
        <ol className={styles['steps']}>
          {steps.map((step, index) => (
            <li key={step.key}>
              <StepEditor
                step={step}
                position={index + 1}
                count={steps.length}
                onChange={(next) => setSteps(steps.map((s) => (s === step ? next : s)))}
                onMove={(direction) => move(index, direction)}
                onRemove={() => setSteps(steps.filter((s) => s !== step))}
              />
            </li>
          ))}
        </ol>
      )}
      <div className={styles['row']}>
        <Select<StepType>
          label={t('games.install.stepType')}
          value={newType}
          options={STEP_TYPES.map((type) => ({
            value: type,
            label: t(`games.install.kinds.${type}`),
          }))}
          onChange={setNewType}
        />
        <Button variant="secondary" onClick={() => setSteps([...steps, newStepDraft(newType)])}>
          {t('games.install.addStep')}
        </Button>
      </div>
      <ErrorText error={retry.error} />
      <div className={styles['row']}>
        <Button type="submit" disabled={steps.length === 0 || retry.isPending}>
          {t('games.import.retryWithSteps')}
        </Button>
      </div>
    </form>
  );
}
